import React from 'react';
import { Button } from '../ui/button';
import { X, CheckCircle, AlertCircle } from 'lucide-react';
import ReportButton from '../ReportButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const getEmbedUrl = (url) => {
  if (!url) return null;
  // YouTube
  let videoId = null;
  const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
  if (shortMatch) videoId = shortMatch[1];
  const watchMatch = url.match(/[?&]v=([a-zA-Z0-9_-]+)/);
  if (!videoId && watchMatch) videoId = watchMatch[1];
  const embedMatch = url.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]+)/);
  if (!videoId && embedMatch) videoId = embedMatch[1];
  // Shorts + Live links (youtube.com/shorts/<id>, youtube.com/live/<id>)
  const shortsMatch = url.match(/youtube\.com\/(?:shorts|live)\/([a-zA-Z0-9_-]+)/);
  if (!videoId && shortsMatch) videoId = shortsMatch[1];
  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}?controls=1&rel=0&modestbranding=1&playsinline=1`;
  }
  // Vimeo — supports vimeo.com/1234 and player.vimeo.com/video/1234
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}?title=0&byline=0&portrait=0`;
  }
  return url;
};

export const isDirectVideoFile = (url) => {
  if (!url) return false;
  if (/youtube\.com|youtu\.be|vimeo\.com|wistia\.com|loom\.com/i.test(url)) return false;
  return /\.(mp4|webm|mov|m4v|ogg|ogv)(\?|#|$)/i.test(url)
    || url.includes('r2.cloudflarestorage')
    || url.includes('/uploads/')
    || url.includes('/api/media/')          // our chunked-upload stream endpoint
    || url.includes('/api/video-library/'); // library stream endpoint
};

/**
 * Build a playable URL for a lesson asset. Backend media/stream endpoints
 * require auth; browser <video>/<iframe> tags can't send an Authorization
 * header — so for our own protected URLs we append `?token=<jwt>` which
 * the backend accepts as a fallback (see stream_media in lms.py).
 */
export const buildAssetUrl = (raw) => {
  if (!raw) return '';
  const absolute = raw.startsWith('http') ? raw : `${API_URL}${raw}`;
  // Only attach the token to our OWN protected asset routes — never to
  // external URLs (YouTube etc.) or absolute S3/R2 URLs that don't need it.
  const needsToken =
    absolute.includes(`${API_URL}/api/media/`) ||
    absolute.includes(`${API_URL}/api/video-library/`);
  if (!needsToken) return absolute;
  let token = '';
  try {
    token = localStorage.getItem('token') || '';
  } catch {
    // localStorage disabled — the URL will 401 and we'll fall through to
    // the error state below.
  }
  if (!token) return absolute;
  const sep = absolute.includes('?') ? '&' : '?';
  return `${absolute}${sep}token=${encodeURIComponent(token)}`;
};

export const LessonPlayer = ({ lesson, onClose, onComplete, onStartQuiz }) => (
  <div className="bg-brand-surface border border-white/5 rounded-xl overflow-hidden" data-testid="lesson-player">
    <div className="p-4 border-b border-border flex justify-between items-center">
      <div>
        <h3 className="text-lg font-semibold text-foreground">{lesson.title}</h3>
        <p className="text-sm text-muted-foreground">{lesson.duration_minutes} minutes</p>
      </div>
      <div className="flex items-center gap-2">
        <ReportButton videoType="lesson" videoId={lesson.id} variant="pill" />
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="w-5 h-5" />
        </Button>
      </div>
    </div>

    <div className="aspect-video bg-slate-900 relative">
      {lesson.content_type === 'video' && lesson.video_url ? (
        isDirectVideoFile(lesson.video_url) ? (
          <video
            src={buildAssetUrl(lesson.video_url)}
            className="w-full h-full"
            controls
            controlsList="nodownload"
            playsInline
            preload="metadata"
            data-testid="lesson-video-player"
          >
            Your browser does not support the video tag.
          </video>
        ) : (
          <iframe
            src={getEmbedUrl(lesson.video_url)}
            className="w-full h-full"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            title={lesson.title}
            data-testid="lesson-video-iframe"
          />
        )
      ) : lesson.content_type === 'pdf' && lesson.pdf_url ? (
        <iframe src={buildAssetUrl(lesson.pdf_url)} className="w-full h-full" title={lesson.title} />
      ) : lesson.content_type === 'text' || lesson.text_content ? (
        <div className="p-8 h-full overflow-y-auto">
          <div className="prose prose-invert max-w-none">
            <p className="text-foreground whitespace-pre-wrap">{lesson.text_content || 'No content available'}</p>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">No content available for this lesson</p>
          </div>
        </div>
      )}
    </div>

    <div className="p-4 flex justify-between items-center">
      {!lesson.is_completed && (
        <Button onClick={() => onComplete(lesson.id)} className="bg-green-600 text-white hover:bg-green-700">
          <CheckCircle className="w-4 h-4 mr-2" /> Mark as Complete
        </Button>
      )}
      {lesson.has_quiz && (
        <Button onClick={() => onStartQuiz(lesson.quiz_id)} className="bg-primary text-primary-foreground hover:bg-primary/90">
          Take Quiz
        </Button>
      )}
    </div>
  </div>
);
